import { useCallback, useRef, useState, useEffect, useMemo } from "react"
import { useLocalization } from "@tm/localization"
import Morpheus from "@tm/morpheus"
import { ErpBundleParams } from "@tm/models"

export const MAX_AMOUNT = 9999

const ceilToDivision = (value: number, division: number): number => Math.ceil(value / division) * division

export type BasketAmountFieldLogicProps = {
    originalQuantity: number
    division: number
    onChangeQuantity?: (value: number, loadErpInfo: boolean) => void
    hasSuggestedQuantity?: boolean
    showQuantityWithDecimals?: boolean
}

export const useBasketAmountFieldLogic = ({
    division,
    originalQuantity,
    onChangeQuantity,
    hasSuggestedQuantity,
    showQuantityWithDecimals,
}: BasketAmountFieldLogicProps) => {
    const { translateText } = useLocalization()
    const avoidErpRequestsForcedByDivisionChange = !!Morpheus.getParams<ErpBundleParams>("erp").erpCacheStaleTimeSeconds

    const getQuantity = useCallback(() => {
        return !showQuantityWithDecimals && division ? ceilToDivision(originalQuantity, division) : originalQuantity
    }, [showQuantityWithDecimals, division, originalQuantity])

    const [amountFieldQuantity, setAmountFieldQuantity] = useState<number | undefined>(getQuantity())
    const inputRef = useRef<HTMLInputElement>(null)
    const keyPressed = useRef<string>("")

    useEffect(() => {
        const updatedQuantity = getQuantity()
        setAmountFieldQuantity(updatedQuantity)
        if (onChangeQuantity && updatedQuantity !== originalQuantity) {
            onChangeQuantity(updatedQuantity, !avoidErpRequestsForcedByDivisionChange)
        }
    }, [avoidErpRequestsForcedByDivisionChange, getQuantity, originalQuantity]) // do not add onChangeQuantity here to prevent endless alerts

    const handleBlur = useCallback(
        (value: number | undefined) => {
            if (keyPressed.current === "Escape") {
                setAmountFieldQuantity(ceilToDivision(originalQuantity, division))
                return
            }

            let finalValue = value || 1
            if (division > 1) {
                finalValue = ceilToDivision(finalValue, division)
            }

            setAmountFieldQuantity(finalValue)

            if (originalQuantity !== finalValue) {
                onChangeQuantity?.(finalValue, true)
            }
        },
        [onChangeQuantity, division, originalQuantity]
    )

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select()
    }, [])

    const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        keyPressed.current = e.code
        if (["Escape", "NumpadEnter", "Enter"].includes(e.code)) {
            ;(e.target as HTMLInputElement).blur()
        }
    }, [])

    const maxAmountValue = useMemo(() => MAX_AMOUNT - (MAX_AMOUNT % division), [division])

    const amountInfo = useMemo(() => {
        if (division > 1) {
            return translateText(13627)
        }
        if (hasSuggestedQuantity) {
            return translateText(13670)
        }
    }, [division, hasSuggestedQuantity, translateText])

    return {
        quantity: amountFieldQuantity,
        setQuantity: setAmountFieldQuantity,
        inputRef,
        handleBlur,
        handleFocus,
        handleKeyUp,
        maxAmountValue,
        amountInfo,
    }
}
